<template>
  <v-layout row justify-center>
    <v-flex xs6>
      <v-dialog v-model="publishDialog" max-width="1000px">
        <v-card>
          <v-card-title class="headline font-weight-light">
            {{ messages.title.PUBLISH_RECORD }}
          </v-card-title>
          <v-card-text>
            <v-alert color="success" icon="check_circle" value="true"
              v-if="isPublishSuccess && publishResponseMsg !== ''">
              <span v-html="publishResponseMsg"></span>
            </v-alert>
            <v-alert color="error" icon="warning" value="true" v-if="!isPublishSuccess && publishResponseMsg !== ''">
              <span v-html="publishResponseMsg"></span>
            </v-alert>
            <!-- Selected Record -->
            <v-layout row wrap>
              <v-flex xs12>
                <v-data-table :headers="headers" :items="recordsToPublish" :loading="publishingInProgress" hide-actions
                  disable-initial-sort>
                  <!-- Items -->
                  <template slot="items" slot-scope="props">
                    <tr>
                      <td class="text-xs-left">{{ props.item.state }}</td>
                      <td class="text-xs-left">{{ props.item.title }}</td>
                      <td class="text-xs-left">{{ props.item.paid }}</td>
                      <td class="text-xs-left">{{ props.item.provider }}</td>
                      <td class="text-xs-left">{{ props.item.manifestId }}</td>
                      <td class="text-xs-left">{{ props.item.metadataVersion }}</td>
                      <td class="text-xs-left">{{ getLocalDateTime(props.item.updated) }}</td>
                    </tr>
                  </template>
                </v-data-table>
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" v-if="isPublishSuccess" @click.native="donePublishing()">{{ messages.label.DONE }}
            </v-btn>
            <div v-else>
              <v-btn color="grey lighten-1 white--text" @click.native="cancelPublishing()">{{ messages.label.CANCEL }}
              </v-btn>
              <v-btn color="primary" :loading="publishingInProgress" @click.native="publishProcessingRecord()">
                {{ messages.label.PUBLISH }}</v-btn>
            </div>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapGetters } from "vuex"
import PreIngestService from "@/PreIngest/PreIngestService"
import * as preIngestDataService from "@/PreIngest/PreIngestDataService"
import * as utils from "@/Shared/utils"
import {
  VOD_SYCN_PREINGEST_WEB_SERVICE_URL
} from "@/store/constants/getter-types"
import messages from "@/PreIngest/messages.json"

export default {
  name: "PublishRecord",
  data() {
    return {
      recordsToPublish: [],
      selectedRecord: {},
      publishDialog: false,
      publishResponseMsg: "",
      isPublishSuccess: false,
      publishingInProgress: false,
      headers: this.getTableHeaders()
    }
  },
  computed: {
    ...mapGetters([
      VOD_SYCN_PREINGEST_WEB_SERVICE_URL
    ])
  },
  methods: {
    /**
     * openPublishDialog - Show dialog to begin publishing process
     * @param  {object} processingRecord selected processing record
     */
    openPublishDialog(processingRecord) {
      this.selectedRecord = processingRecord
      this.resetData()
      this.recordsToPublish.push(this.selectedRecord)
      this.publishDialog = true
    },
    /**
     * cancelPublishing - Close the publishing dialog
     */
    cancelPublishing() {
      this.publishDialog = false
      this.resetData()
    },
    /**
     * resetData - Cleanup data. required to be done before opening / closing the modal
     */
    resetData() {
      this.recordsToPublish = []
      this.publishResponseMsg = ""
      this.isPublishSuccess = false
    },
    /**
     * publishProcessingRecord - Publish the selected processing record
     */
    publishProcessingRecord() {
      this.publishingInProgress = true
      let reqBody = {
        publish: {
          processingRecordId: this.selectedRecord.id
        }
      }
      preIngestDataService.publishProcessingRecord(reqBody,
        this.VOD_SYCN_PREINGEST_WEB_SERVICE_URL)
        .then(() => {
          this.publishingInProgress = false
          this.isPublishSuccess = true
          //  let publishManifestResponse = response.data.publishManifestResponse
          this.publishResponseMsg = this.messages.info.PUBLISH_SUCCESS
        })
        .catch((error) => {
          this.publishingInProgress = false
          this.isPublishSuccess = false
          let response = error.response.data
          if (
            response.isException &&
            !this.$lodash.isEmpty(response.description)
          ) {
            this.publishResponseMsg = this.messages.error.PUBLISH_FAILURE
            this.publishResponseMsg += `<br/><b>Description:</b>${response.description
              }`
          } else {
            this.publishResponseMsg = this.messages.error.PUBLISH_FAILURE
          }
        })
    },
    /**
     * donePublishing - Handle publishing completion
     */
    donePublishing() {
      this.publishDialog = false
      let refreshReason = {
        publish: true,
        publishedRecordId: this.selectedRecord.id.split()
      }
      //  Refresh search results and pass data to highlight published record
      PreIngestService.$emit("event:refreshSearchResult", refreshReason)
    },
    /**
     * getTableHeaders - Retrieve table headers
     *
     * @return {array}  array of objects describing table columns
     */
    getTableHeaders() {
      let headers = [
        {
          text: messages.label.TH_STATE,
          align: "left",
          value: "state",
          sortable: false
        },
        {
          text: messages.label.TH_TITLE,
          align: "left",
          value: "title",
          sortable: false
        },
        {
          text: messages.label.TH_PACKAGE_ID,
          align: "left",
          value: "paid",
          sortable: false
        },
        {
          text: messages.label.TH_PROVIDER,
          align: "left",
          value: "provider",
          sortable: false
        },
        {
          text: messages.label.TH_MANIFEST_ID,
          align: "left",
          value: "manifestId",
          sortable: false
        },
        {
          text: messages.label.TH_METADATA_VERSION,
          align: "left",
          value: "metadataVersion",
          sortable: false
        },
        {
          text: messages.label.TH_LAST_UPDATED,
          align: "left",
          value: "updated",
          sortable: false
        }
      ]
      return headers
    },
    /**
     * getLocalDateTime - Retrieve local date time
     *
     * @param  {Number} dateTime unix timestamp
     * @return {string}          date time string in local time
     */
    getLocalDateTime(dateTime) {
      return utils.getLocalDateTime(dateTime)
    }
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    /**
     * Events
     * */
    PreIngestService.$on("event:showPublishRecord", (processingRecord) => {
      this.openPublishDialog(processingRecord)
    })
  }
}
</script>

<style lang="css">
</style>



// WEBPACK FOOTER //
// src/PreIngest/ProcessingRecords/PublishRecord.vue